import { API_ROUTES } from 'core/api'
import { API_BASE_URL } from 'core/environments'
import { getUserTokenFromLocalStorage } from 'utils'
import { APIRequest, RequestOptionsType } from 'utils/requests'
import { getSurveyReportQueryParams } from '../pages/beheardReport/BeheardReport'
export type APIOptions = {
  queryParams?: any
  pathParams?: any
  body?: any
}

export const API = {
  base: (
    url: keyof typeof API_ROUTES,
    options: Omit<RequestOptionsType, 'path'>,
  ) =>
    APIRequest({
      path: API_ROUTES[url],
      ...(options ?? {}),
    }),
  get: (
    url: keyof typeof API_ROUTES,
    id?: string | number | null,
    options?: APIOptions,
  ) =>
    APIRequest({
      path: API_ROUTES[url],
      method: 'GET',
      pathParams: {
        ...(id && { id: id }),
        ...(options ?? {}).pathParams,
      },
      ...options,
    }),
  create: (
    url: keyof typeof API_ROUTES,
    payload?: Record<any, unknown> | any,
    options?: APIOptions,
  ) =>
    APIRequest({
      path: API_ROUTES[url],
      method: 'POST',
      body: payload,
      pathParams: {
        ...((payload ?? {}).id && { id: payload.id }),
      },
      ...options,
    }),
  update: (
    url: keyof typeof API_ROUTES,
    payload?: Record<any, unknown> | any,
    options?: APIOptions,
  ) =>
    APIRequest({
      path: API_ROUTES[url],
      method: 'PATCH',
      body: payload,
      pathParams: {
        ...((payload ?? {}).id && { id: payload.id }),
      },
      ...options,
    }),
  createOrUpdate: (
    url: keyof typeof API_ROUTES,
    payload?: Record<any, unknown> | any,
    payloadId?: string,
    options?: APIOptions,
  ) => {
    const id = payloadId ?? payload.id

    return APIRequest({
      path: API_ROUTES[url],
      method: id ? 'PATCH' : 'POST',
      body: payload,
      pathParams: {
        ...(id && { id: id }),
      },
      ...options,
    })
  },
  delete: (
    url: keyof typeof API_ROUTES,
    id: string | null,
    options?: APIOptions,
  ) =>
    APIRequest({
      path: API_ROUTES[url],
      method: 'DELETE',
      pathParams: (options ?? {}).pathParams ?? {
        id: id,
      },
      ...(options ?? {}),
    }),
}

export const downloadOpenEndedAnswersRequest = (
  survey_id,
  shared_report_id,
  open_ended_download_format,
  filterState,
) => {
  const request = new XMLHttpRequest()
  request.open(
    'POST',
    `${API_BASE_URL}${API_ROUTES.downloadOpenEndedAnswers}`,
    true,
  )
  // @ts-ignore
  request.setRequestHeader('Authorization', getUserTokenFromLocalStorage())
  request.responseType = 'blob'
  request.onload = function () {
    if (this.status === 200) {
      const file = window.URL.createObjectURL(this.response)
      const a = document.createElement('a')
      a.href = file
      a.download = this.response.name || 'Survey-OpenEnded-Answers'
      document.body.appendChild(a)
      a.click()

      document.body.removeChild(a)
    }
  }

  const requestData: any = {
    survey_id: survey_id,
    type: open_ended_download_format,
  }
  if (filterState?.criteria.length) {
    requestData.filters = filterState
  }

  if (shared_report_id) {
    requestData.shared_report_id = shared_report_id
  }
  request.setRequestHeader('Content-Type', 'application/json')

  request.send(JSON.stringify(requestData))
}

export const downloadPremiumReportRequest = ({
  surveyId,
  type,
  filters,
  setIsPdfReportLoading,
  openInNewTab,
}) => {
  const request = new XMLHttpRequest()
  request.open(
    'POST',
    `${API_BASE_URL}${API_ROUTES.pdfPremiumReport}.${type}`,
    true,
  )
  // @ts-ignore
  request.setRequestHeader('Authorization', getUserTokenFromLocalStorage())
  request.responseType = 'blob'
  setIsPdfReportLoading(true)

  request.onload = function () {
    setIsPdfReportLoading(false)
    if (this.status === 200) {
      const file = window.URL.createObjectURL(this.response)
      const a = document.createElement('a')
      a.href = file

      if (openInNewTab) {
        window.open(file, '_blank')
      } else {
        a.download = `beheard_report_${+new Date()}`

        a.rel = 'noopener noreferrer'
        a.target = '_blank'

        document.body.appendChild(a)
        a.click()

        document.body.removeChild(a)
      }
    }
  }
  const requestData = {
    survey_id: surveyId,
    filters: getSurveyReportQueryParams(filters),
    type: type,
  }

  request.setRequestHeader('Content-Type', 'application/json')
  request.send(JSON.stringify(requestData))
}

export const downloadPulseSurveyReportRequest = ({
  surveyId,
  type,
  filtersState,
  setIsReportLoading,
  openInNewTab,
}: {
  surveyId: number
  type: 'pdf' | 'xlsx'
  filtersState: Record<string, any[]>
  setIsReportLoading: (loading: boolean) => void
  openInNewTab: boolean
}) => {
  const request = new XMLHttpRequest()
  request.open(
    'POST',
    `${API_BASE_URL}${API_ROUTES.pulseSurveyReport}.${type}`,
    true,
  )
  // @ts-ignore
  request.setRequestHeader('Authorization', getUserTokenFromLocalStorage())
  request.responseType = 'blob'
  setIsReportLoading(true)

  request.onload = function () {
    setIsReportLoading(false)
    if (this.status === 200) {
      const file = window.URL.createObjectURL(this.response)
      const a = document.createElement('a')
      a.href = file

      if (openInNewTab) {
        window.open(file, '_blank')
      } else {
        a.download = `pulse_survey_report_${+new Date()}`
        a.rel = 'noopener noreferrer'
        a.target = '_blank'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    }
  }

  const criteria = Object.keys(filtersState)
    .filter(key => filtersState[key].length > 0)
    .map(key => ({ category: +key, demographics: filtersState[key] }))

  const requestData = { survey_id: surveyId, type, criteria }
  request.setRequestHeader('Content-Type', 'application/json')
  request.send(JSON.stringify(requestData))
}

export const downloadPulseSurveyParticipationReportRequest = (
  surveyId: number,
  setIsLoading?: (isLoading: boolean) => void,
) => {
  setIsLoading?.(true)
  const url = `${API_BASE_URL}${API_ROUTES.pulseSurveyParticipationReport.replace(':id', String(surveyId))}`
  const request = new XMLHttpRequest()
  request.open('GET', url, true)
  // @ts-ignore
  request.setRequestHeader('Authorization', getUserTokenFromLocalStorage())
  request.responseType = 'blob'
  request.onload = function () {
    setIsLoading?.(false)
    if (this.status === 200) {
      const disposition = request.getResponseHeader('Content-Disposition') || ''
      const match = /filename="?([^"]+)"?/.exec(disposition)
      const filename = match?.[1] || 'participation-report.csv'
      const objectUrl = window.URL.createObjectURL(this.response)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(objectUrl)
    }
  }
  request.onerror = function () {
    setIsLoading?.(false)
  }
  request.send()
}

export const recognitionActionRequestfile = (id, reminderDate) => {
  const request = new XMLHttpRequest()
  request.open('PATCH', `${API_BASE_URL}recognition/actions/${id}`, true)
  // @ts-ignore
  request.setRequestHeader('Authorization', getUserTokenFromLocalStorage())
  request.setRequestHeader('Content-Type', 'application/json')

  request.responseType = 'text'

  request.onreadystatechange = function () {
    if (this.readyState === XMLHttpRequest.DONE && this.status === 200) {
      const blob = new Blob([this.response], {
        type: 'text/calendar;charset=utf-8',
      })
      // window.navigator.msSaveOrOpenBlob(blob, 'reminder.ics')

      const file = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = file
      a.download = this.response.name || 'Employee-Recognition-Reminder'
      document.body.appendChild(a)
      a.click()

      document.body.removeChild(a)
    }
  }

  request.send(`{ "reminder_date": "${reminderDate.toISOString()}" }`)
}

export const downloadExternalRewardsTemplate = () => {
  const request = new XMLHttpRequest()

  request.open(
    'GET',
    `${API_BASE_URL}${API_ROUTES.downloadExternalRewardsTemplate}`,
    true,
  )
  // @ts-ignore
  request.setRequestHeader('Authorization', getUserTokenFromLocalStorage())
  request.responseType = 'blob'
  request.onload = function () {
    if (this.status === 200) {
      const file = window.URL.createObjectURL(this.response)
      const a = document.createElement('a')
      a.href = file
      a.download = this.response.name || 'Sparck External Rewards Template'
      document.body.appendChild(a)
      a.click()

      document.body.removeChild(a)
    }
  }
  request.send()
}

export const downloadRewardsReport = () => {
  const request = new XMLHttpRequest()

  request.open(
    'GET',
    `${API_BASE_URL}${API_ROUTES.downloadRewardsReport}`,
    true,
  )
  // @ts-ignore
  request.setRequestHeader('Authorization', getUserTokenFromLocalStorage())
  request.responseType = 'blob'
  request.onload = function () {
    if (this.status === 200) {
      const file = window.URL.createObjectURL(this.response)
      const a = document.createElement('a')
      a.href = file
      a.download = this.response.name || 'Sparck Rewards Report'
      document.body.appendChild(a)
      a.click()

      document.body.removeChild(a)
    }
  }
  request.send()
}

export const downloadBudgetActivity = () => {
  const request = new XMLHttpRequest()

  request.open(
    'GET',
    `${API_BASE_URL}${API_ROUTES.downloadBudgetActivity}`,
    true,
  )
  // @ts-ignore
  request.setRequestHeader('Authorization', getUserTokenFromLocalStorage())
  request.responseType = 'blob'
  request.onload = function () {
    if (this.status === 200) {
      const file = window.URL.createObjectURL(this.response)
      const a = document.createElement('a')
      a.href = file
      a.download = this.response.name || 'Sparck Budget Activity'
      document.body.appendChild(a)
      a.click()

      document.body.removeChild(a)
    }
  }
  request.send()
}

export const downloadSurveyAdminDataRequest = (
  surveyId: string,
  onProgress?: (progress: number, status: string) => void,
  onError?: (error: string) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const token = getUserTokenFromLocalStorage()
    if (!token) {
      const errorMessage = 'Authentication token not found'
      onError?.(errorMessage)
      reject(new Error(errorMessage))
      return
    }

    const request = new XMLHttpRequest()
    request.open(
      'GET',
      `${API_BASE_URL}${API_ROUTES.downloadSurvey.replace(':id', surveyId)}`,
      true,
    )
    request.setRequestHeader('Authorization', token)
    request.responseType = 'text'

    request.onload = function () {
      if (this.status === 202) {
        try {
          const response = JSON.parse(this.responseText)
          const progressMessage = `Export started... (${response.progress || 0}%)`
          onProgress?.(response.progress || 0, progressMessage)

          pollDownloadStatus(
            surveyId,
            response.job_id,
            resolve,
            reject,
            onProgress,
            onError,
            response.filename,
          )
        } catch (e) {
          const errorMessage = 'Failed to parse server response'
          onError?.(errorMessage)
          reject(new Error(errorMessage))
        }
      } else if (this.status === 200) {
        downloadFile(this.response, this.response.filename)
        onProgress?.(100, 'Download completed (100%)')
        resolve()
      } else {
        const errorMessage = `Download failed: ${this.status}`
        onError?.(errorMessage)
        reject(new Error(errorMessage))
      }
    }

    request.onerror = function () {
      const errorMessage = 'There was an error with the request'
      onError?.(errorMessage)
      reject(new Error(errorMessage))
    }

    request.send()
  })
}

const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

const pollDownloadStatus = (
  surveyId: string,
  jobId: string,
  resolve: () => void,
  reject: (error: Error) => void,
  onProgress?: (progress: number, status: string) => void,
  onError?: (error: string) => void,
  filename = '',
) => {
  const pollInterval = 10000

  const token = getUserTokenFromLocalStorage()
  if (!token) {
    const errorMessage = 'Authentication token not found'
    onError?.(errorMessage)
    reject(new Error(errorMessage))
    return
  }

  const request = new XMLHttpRequest()
  request.open(
    'GET',
    `${API_BASE_URL}${API_ROUTES.downloadSurveyStatus.replace(':id', surveyId)}?job_id=${jobId}`,
    true,
  )
  request.setRequestHeader('Authorization', token)
  request.responseType = 'blob'

  request.onload = function () {
    const contentType = request.getResponseHeader('Content-Type')

    if (contentType?.includes('application/json')) {
      const reader = new FileReader()

      reader.onload = () => {
        let response: any = {}
        try {
          response = JSON.parse(reader.result as string)
        } catch {
          response = { progress: 0, message: 'Processing...' }
        }

        // const progress = response.progress || 0
        // const message = `${response.message || 'Exporting...'} (${progress}%)`
        // onProgress?.(progress, message)

        const progress = response.progress || 0
        const step = response.current_step || ''
        const message = `Exporting ${step} (${progress}%)`
        onProgress?.(progress, message)


        const updatedFilename = response.filename || filename

        setTimeout(() => {
          pollDownloadStatus(
            surveyId,
            jobId,
            resolve,
            reject,
            onProgress,
            onError,
            updatedFilename,
          )
        }, pollInterval)
      }

      reader.onerror = () => {
        const errorMessage = 'Error reading server response'
        onError?.(errorMessage)
        reject(new Error(errorMessage))
      };

      reader.readAsText(request.response)
    } else if (contentType?.includes(
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    )) {
      downloadFile(request.response, filename || 'export.xlsx')
      onProgress?.(100, 'Download completed! (100%)')
      resolve()
    } else {
      const errorMessage = `Unexpected content type: ${contentType}`
      onError?.(errorMessage)
      reject(new Error(errorMessage))
    }
  }

  request.onerror = function () {
    const errorMessage = 'Something went wrong with the request'
    onError?.(errorMessage)
    reject(new Error(errorMessage))
  };

  request.send()
}

// const pollDownloadStatus = (
//   surveyId: string,
//   jobId: string,
//   resolve: () => void,
//   reject: (error: Error) => void,
//   onProgress?: (progress: number, status: string) => void,
//   onError?: (error: string) => void,
//   attempts = 0,
//   filename = '',
// ) => {
//   const maxAttempts = 60
//   const pollInterval = 5000

//   if (attempts >= maxAttempts) {
//     const errorMessage = 'Timeout while polling download status'
//     onError?.(errorMessage)
//     reject(new Error(errorMessage))
//     return
//   }

//   const token = getUserTokenFromLocalStorage()
//   if (!token) {
//     const errorMessage = 'Authentication token not found'
//     onError?.(errorMessage)
//     reject(new Error(errorMessage))
//     return
//   }

//   const request = new XMLHttpRequest()
//   request.open(
//     'GET',
//     `${API_BASE_URL}${API_ROUTES.downloadSurveyStatus.replace(':id', surveyId)}?job_id=${jobId}`,
//     true,
//   )
//   request.setRequestHeader('Authorization', token)
//   request.responseType = 'blob'

//   request.onload = function () {
//     const contentType = request.getResponseHeader('Content-Type')

//     if (contentType?.includes('application/json')) {
//       const reader = new FileReader();

//       reader.onload = () => {
//         let response: any = {}
//         try {
//           response = JSON.parse(reader.result as string)
//         } catch {
//           response = { progress: 0, message: 'Processing...' }
//         }

//         const progress = response.progress || 0
//         const message = `${response.message || 'Exporting...'} (${progress}%)`
//         onProgress?.(progress, message)

//         const updatedFilename = response.filename || filename

//         setTimeout(() => {
//           pollDownloadStatus(
//             surveyId,
//             jobId,
//             resolve,
//             reject,
//             onProgress,
//             onError,
//             attempts + 1,
//             updatedFilename,
//           )
//         }, pollInterval)
//       }

//       reader.onerror = () => {
//         const errorMessage = 'Error reading server response'
//         onError?.(errorMessage)
//         reject(new Error(errorMessage))
//       };

//       reader.readAsText(request.response)
//     }
//     else if (contentType?.includes(
//       'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
//     )) {
//       downloadFile(request.response, filename || 'export.xlsx')
//       onProgress?.(100, 'Download completed! (100%)')
//       resolve()
//     }
//     else {
//       const errorMessage = `Unexpected content type: ${contentType}`
//       onError?.(errorMessage);
//       reject(new Error(errorMessage))
//     }
//   }

//   request.onerror = function () {
//     const errorMessage = 'Something went wrong with the request';
//     onError?.(errorMessage);
//     reject(new Error(errorMessage))
//   }

//   request.send()
// }
